import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { API } from "../../types";
import { EntityStatus } from "../../types";
import { formatApiDisplayName } from "../../utils/apiDisplayName";

interface ApiExplorerViewsProps {
  api: API;
}

export const ApiExplorerCard: React.FC<ApiExplorerViewsProps> = ({ api }) => {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 3 },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/api-explorer/${api.uuid}`}
        sx={{ height: "100%", alignItems: "stretch" }}
      >
        <CardContent>
          <Chip
            label={api.status}
            size="small"
            color={api.status === EntityStatus.ACTIVE ? "success" : "default"}
            sx={{ mb: 1 }}
          />
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            title={api.name}
            sx={{ wordBreak: "break-word" }}
          >
            {formatApiDisplayName(api.name)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Version: {api.version || "—"}
          </Typography>
          {api.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {api.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const ApiExplorerListRow: React.FC<ApiExplorerViewsProps> = ({ api }) => {
  return (
    <Box
      component={RouterLink}
      to={`/api-explorer/${api.uuid}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          title={api.name}
          sx={{ wordBreak: "break-word" }}
        >
          {formatApiDisplayName(api.name)}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {api.description || "No description"}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
        {api.version}
      </Typography>
      <Chip
        label={api.status}
        size="small"
        color={api.status === EntityStatus.ACTIVE ? "success" : "default"}
      />
    </Box>
  );
};
